import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Row, Col, Image } from "react-bootstrap";
import { adminService } from "../../services/adminService";
import ImageUploadField from "../../components/Common/ImageUploadField";
import { IMAGE_SPECS } from "../../utils/imageSpecs";

interface BannerData {
  id?: string;
  title: string;
  description: string;
  position: string;
  status: string;
  startDate: string;
  endDate: string;
  imageUrl?: string;
  linkUrl?: string;
  posterImage?: string;
  trailerUrl?: string;
  certificate?: string;
  duration?: string;
  language?: string;
}

interface BannerModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (banner: BannerData) => void;
  banner?: BannerData | null;
  mode: "add" | "edit";
}

const BannerModal: React.FC<BannerModalProps> = ({
  show,
  onHide,
  onSave,
  banner,
  mode,
}) => {
  const [formData, setFormData] = useState<BannerData>({
    title: "",
    description: "",
    position: "Hero",
    status: "Active",
    startDate: "",
    endDate: "",
    imageUrl: "",
    linkUrl: "",
    posterImage: "",
    trailerUrl: "",
    certificate: "",
    duration: "",
    language: "",
  });

  // Store original banner data for comparison
  const [originalBanner, setOriginalBanner] = useState<BannerData | null>(null);

  useEffect(() => {
    if (banner && mode === "edit") {
      // Set both form data and original banner data
      const bannerData = {
        title: banner.title || "",
        description: banner.description || "",
        position: banner.position || "Hero",
        status: banner.status || "Active",
        startDate: banner.startDate
          ? new Date(banner.startDate).toISOString().split("T")[0]
          : "",
        endDate: banner.endDate
          ? new Date(banner.endDate).toISOString().split("T")[0]
          : "",
        imageUrl: banner.imageUrl || "",
        linkUrl: banner.linkUrl || "",
        posterImage: banner.posterImage || "",
        trailerUrl: banner.trailerUrl || "",
        certificate: banner.certificate || "",
        duration: banner.duration || "",
        language: banner.language || "",
      };
      setFormData(bannerData);
      setOriginalBanner(bannerData);
    } else if (mode === "add") {
      setFormData({
        title: "",
        description: "",
        position: "Hero",
        status: "Active",
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        imageUrl: "",
        linkUrl: "",
        posterImage: "",
        trailerUrl: "",
        certificate: "",
        duration: "",
        language: "",
      });
      setOriginalBanner(null);
    }
  }, [banner, mode, show]);

  const handleInputChange = (field: keyof BannerData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const uploadBannerFile = async (file: File): Promise<string | null> => {
    const response = await adminService.uploadBannerImage(file);
    if (response.success && response.data?.url) {
      return response.data.url;
    }
    throw new Error(response.error || "Upload failed");
  };

  const bannerSpec = useMemo(() => {
    if (formData.position === "Promotional") return IMAGE_SPECS.bannerPromo;
    if (formData.position === "Sidebar") return IMAGE_SPECS.bannerSidebar;
    return IMAGE_SPECS.bannerHero;
  }, [formData.position]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // For edit mode, only send changed fields
    let submissionData: BannerData;
    
    if (mode === 'edit' && originalBanner) {
      // Only include fields that have actually changed
      const changedFields: Partial<BannerData> = {};
      
      // Compare each field with original
      (Object.keys(formData) as Array<keyof BannerData>).forEach(key => {
        if (formData[key] !== originalBanner[key]) {
          changedFields[key] = formData[key];
        }
      });
      
      // Always include the ID for updates
      submissionData = {
        id: banner?.id,
        ...changedFields
      } as BannerData;
      
      console.log('Edit mode - changed fields:', changedFields);
    } else {
      // Add mode - send all form data
      submissionData = formData;
    }
    
    onSave(submissionData);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "add" ? "Create New Banner" : "Edit Banner"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  required
                  placeholder="Enter banner title"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Position</Form.Label>
                <Form.Select
                  value={formData.position}
                  onChange={(e) =>
                    handleInputChange("position", e.target.value)
                  }
                >
                  <option value="Hero">Hero</option>
                  {/* <option value="Featured">Featured</option> */}
                  {/* <option value="Promotional">Promotional</option> */}
                  {/* <option value="Sidebar">Sidebar</option> */}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Brief description of the banner"
              maxLength={70}
            />
            <Form.Text className="text-muted">
              Maximum 70 characters. Current: {formData.description.length}/70
            </Form.Text>
          </Form.Group>

          <Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Trailer YouTube URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.trailerUrl}
                    onChange={(e) =>
                      handleInputChange("trailerUrl", e.target.value)
                    }
                    placeholder="https://youtube.com/watch?v=xxxx"
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Certificate</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.certificate}
                    onChange={(e) =>
                      handleInputChange("certificate", e.target.value)
                    }
                    placeholder="U/A"
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.duration}
                    onChange={(e) =>
                      handleInputChange("duration", e.target.value)
                    }
                    placeholder="1h 50min"
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Language</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.language}
                    onChange={(e) =>
                      handleInputChange("language", e.target.value)
                    }
                    placeholder="Malayalam"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Banner Image</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) =>
                      handleInputChange("imageUrl", e.target.value)
                    }
                    placeholder="https://example.com/banner.jpg or upload image"
                  />
                  <ImageUploadField
                    spec={bannerSpec}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onUpload={uploadBannerFile}
                    onChange={(url) => handleInputChange("imageUrl", url)}
                    fieldId="banner-upload-input"
                  />
                </div>
                <Form.Text className="text-muted">
                  You can either enter a URL or upload an image file.
                </Form.Text>
                {formData.imageUrl && (
                  <div className="mt-2">
                    <Image
                      src={formData.imageUrl}
                      alt="Banner preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        objectFit: "contain",
                      }}
                      className="img-thumbnail"
                      onError={(e: any) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Poster Image</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="url"
                    value={formData.posterImage}
                    onChange={(e) =>
                      handleInputChange("posterImage", e.target.value)
                    }
                    placeholder="https://example.com/poster.jpg or upload"
                  />
                  <ImageUploadField
                    spec={IMAGE_SPECS.bannerHeroPoster}
                    onUpload={uploadBannerFile}
                    onChange={(url) => handleInputChange("posterImage", url)}
                    fieldId="poster-upload-input"
                  />
                </div>

                {formData.posterImage && (
                  <div className="mt-2">
                    <Image
                      src={formData.posterImage}
                      alt="Poster preview"
                      style={{
                        width: "180px",
                        height: "270px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                      }}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Link URL</Form.Label>
                <Form.Control
                  type="url"
                  value={formData.linkUrl}
                  onChange={(e) => handleInputChange("linkUrl", e.target.value)}
                  placeholder="https://example.com/destination"
                />
                <Form.Text className="text-muted">
                  Where the banner should link to
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Scheduled">Scheduled</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.startDate || ""}
                  onChange={(e) =>
                    handleInputChange("startDate", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>End Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.endDate || ""}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {mode === "add" ? "Create Banner" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BannerModal;
